import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MatTableDataSource} from "@angular/material/table";
import {OfwcaWithUser360Profiles, User} from "../../../../shared/interface/user";
import {Ofwca} from "../../../../shared/interface/ofwca";
import {EmailAddress} from "../../../../shared/interface/email-address";
import {FacilityDobryAgent, ImageResponsive} from "../../../../shared/interface/facility-dobry-agent";
import {environment} from "../../../../../environments/environment";
import {AuthService} from "../../../../shared/services/auth.service";
import {UserService} from "../../../../shared/services/user.service";
import {Observable, of, ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../../shared/interface/user-permissions";
import {catchError, map, switchMap, takeUntil} from "rxjs/operators";

@Component({
  selector: 'app-dobry-agent-agent-list',
  templateUrl: './dobry-agent-agent-list.component.html',
  styleUrls: ['./dobry-agent-agent-list.component.scss']
})
export class DobryAgentAgentListComponent implements OnInit, OnDestroy {
  @Input() ofwcas: Observable<Ofwca[]>;
  @Input() facilityDobryAgent: Observable<FacilityDobryAgent>;
  @Output() modifyClicked = new EventEmitter<any>();
  ofwcasDataSource: MatTableDataSource<OfwcaWithUser360Profiles> = new MatTableDataSource<OfwcaWithUser360Profiles>([]);
  displayedColumns: string[] = ['fullName', 'status', 'portalDa', 'phone', 'email', 'blog', 'edit', 'avatar'];
  facilityDobryAgentObject?: FacilityDobryAgent = null;
  dobryAgentBaseUrl = environment.dobryAgentUrl;
  userPermissions: ReplaySubject<UserPermissions>;
  loading = true;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private readonly applicationRoleOrder = [
    'none',
    'active',
  ];

  constructor(
    private auth: AuthService,
    private userService: UserService,
  ) {
    this.userPermissions = auth.getUserPermissions();

  }

  ngOnInit(): void {
    this.facilityDobryAgent
      .pipe(takeUntil(this.destroyed$))
      .subscribe((facilityDobryAgent: FacilityDobryAgent) => {
        this.facilityDobryAgentObject = facilityDobryAgent;
      });

    this.ofwcas
      .pipe(
        switchMap((ofwcas: Ofwca[]) => this.loadUsers360Profiles(ofwcas)),
        takeUntil(this.destroyed$)
      )
      .subscribe((ofwcas: OfwcaWithUser360Profiles[]) => {
        this.ofwcasDataSource.data = ofwcas;
        this.loading = false;
      });
  }

  modify(): void {
    this.modifyClicked.emit();
  }


  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private loadUsers360Profiles(ofwcas: Ofwca[]): Observable<OfwcaWithUser360Profiles[]> {
    const userIds = Array.from(new Set(
      (ofwcas ?? [])
        .reduce((ids: string[], ofwca: Ofwca) => {
          (ofwca.user360 ?? []).forEach((user) => {
            if (user?.uuid) {
              ids.push(user.uuid);
            }
          });

          return ids;
        }, [])
    ));

    if (!userIds.length) {
      return of(this.mapOfwcasWithUsers(ofwcas, []));
    }

    return this.userService.getDobryAgentUsers(userIds).pipe(
      map((response) => this.mapOfwcasWithUsers(ofwcas, response?.items ?? [])),
      catchError(() => of(this.mapOfwcasWithUsers(ofwcas, [])))
    );
  }

  private mapOfwcasWithUsers(
    ofwcas: Ofwca[],
    users: User[]
  ): OfwcaWithUser360Profiles[] {
    const usersByUuid = new Map<string, User>();

    (users ?? []).forEach((user: User) => {
      if (user?.uuid) {
        usersByUuid.set(user.uuid, user);
      }
    });

    return (ofwcas ?? []).map((ofwca: Ofwca) => ({
      ...ofwca,
      user360Profiles: (ofwca.user360 ?? [])
        .map((user) => user?.uuid ? usersByUuid.get(user.uuid) : null)
        .filter((user): user is User => !!user),
    } as OfwcaWithUser360Profiles));
  }
  getDobryAgentCoordinator(ofwca: Ofwca): any | null {
    const coordinators = this.facilityDobryAgentObject?.coordinators ?? [];

    return coordinators.find((coordinator: any) => {
      return coordinator.ofwca24Id === ofwca.uuid;
    }) ?? null;
  }

  getDobryAgentOrOfwcaPhone(element: Ofwca): string {
    const coordinator = this.getDobryAgentCoordinator(element);

    if (coordinator?.phone) {
      return coordinator.phone;
    }

    return element?.phone ?? '';
  }

  getDobryAgentOrOfwcaEmail(element: Ofwca): string {
    const coordinator = this.getDobryAgentCoordinator(element);

    if (coordinator?.email) {
      return coordinator.email;
    }

    const emails = element?.emails ?? [];

    const email = emails.find((email: EmailAddress) => email.main) ?? emails[0];

    if (!email) {
      return '';
    }

    return email.address ?? email.name ?? '';
  }


  isOfwcaActiveCoordinator(ofwca: Ofwca): boolean {
    const coordinator = this.getDobryAgentCoordinator(ofwca);

    return !!coordinator && coordinator.active === true;
  }

  hasDobryAgentBlogAccess(element: any): boolean {
    return this.getDobryAgentPermission(
      element,
      'blog'
    ) === 'active';
  }


  hasDobryAgentApplicationAccess(element: any): boolean {
    return this.getDobryAgentPermission(element, 'application_access') === 'active';
  }

  getDobryAgentPermission(element: any, module: string): string {
    const user = this.getPrimaryUser360(element);

    if (!user) {
      return 'none';
    }

    const permissions = this.getEffectiveDobryAgentPermissionsFromUser(user);

    return permissions?.[module] || 'none';
  }
  hasDobryAgentBusinessCardEditAccess(element: any): boolean {
    return this.getDobryAgentPermission(
      element,
      'business_card'
    ) === 'active';
  }


  getAgentAvatarUrl(element: OfwcaWithUser360Profiles): string {
    const previewUrl = (element as any)?.avatarPreviewUrl;

    if (previewUrl) {
      return previewUrl;
    }

    const coordinator = this.getDobryAgentCoordinator(element);

    const imageUrl =
      this.getImageVariantUrl(coordinator?.featuredImage, 'medium')
      ?? coordinator?.featuredImage?.src
      ?? this.getImageVariantUrl((element as any)?.featuredImage, 'medium')
      ?? (element as any)?.featuredImage?.src
      ?? '';

    if (!imageUrl) {
      return '';
    }

    if (
      imageUrl.startsWith('http://') ||
      imageUrl.startsWith('https://') ||
      imageUrl.startsWith('data:image')
    ) {
      return imageUrl;
    }

    return `${this.dobryAgentBaseUrl.replace(/\/$/, '')}/${imageUrl.replace(/^\//, '')}`;
  }

  private getPrimaryUser360(element: OfwcaWithUser360Profiles): User | null {
    if (!element?.user360Profiles || element.user360Profiles.length === 0) {
      return null;
    }

    return element.user360Profiles[0];
  }

  private getImageVariantUrl(
    image: ImageResponsive | null | undefined,
    preferredSize: 'small' | 'medium' | 'large' | 'original' = 'medium'
  ): string | null {
    if (!image) {
      return null;
    }

    const preferredVariant = image.variants?.find(
      (variant) => variant.size === preferredSize
    );

    if (preferredVariant?.url) {
      return preferredVariant.url;
    }

    return image.src ?? null;
  }

  private hasRoleAtLeast(role: string, minimumRole: string): boolean {
    return this.getRolePriority(role) >= this.getRolePriority(minimumRole);
  }


  private getEffectiveDobryAgentPermissionsFromUser(user: User): any {
    if (!user) {
      return {};
    }

    const userPermissions = this.normalizeApplicationPermissions(user.applicationPermissions);
    const userDobryAgentPermissions = userPermissions?.dobryagent || {};

    let departmentDobryAgentPermissions = {};

    if (user.department?.applicationPermissions) {
      const departmentPermissions = this.normalizeDepartmentApplicationPermissions(
        user.department.applicationPermissions,
        'employee'
      );

      departmentDobryAgentPermissions = departmentPermissions?.dobryagent || {};
    }

    return {
      /**
       * Dostęp do aplikacji może pochodzić z działu albo z użytkownika.
       * Najpierw bierzemy dział, a jeśli go nie ma, indywidualne.
       */
      application_access:
        departmentDobryAgentPermissions?.['application_access']
        ?? userDobryAgentPermissions?.['application_access']
        ?? 'none',

      /**
       * Blog zawsze indywidualnie.
       */
      blog: userDobryAgentPermissions?.['blog'] ?? 'none',

      /**
       * Wizytówka zawsze indywidualnie.
       */
      business_card: userDobryAgentPermissions?.['business_card'] ?? 'none',
    };
  }

  private normalizeApplicationPermissions(applicationPermissions: any): any {
    const result = {};

    if (!applicationPermissions || !Array.isArray(applicationPermissions)) {
      return result;
    }

    applicationPermissions.forEach((item: any) => {
      if (item.applicationKey) {
        result[item.applicationKey] = item.permissions || {};
      }
    });

    return result;
  }

  private normalizeDepartmentApplicationPermissions(
    applicationPermissions: any,
    assignmentType: 'employee' | 'supervisor'
  ): any {
    const result = {};

    if (!applicationPermissions || !Array.isArray(applicationPermissions)) {
      return result;
    }

    applicationPermissions
      .filter((item: any) => item.assignmentType === assignmentType)
      .forEach((item: any) => {
        result[item.applicationKey] = item.permissions || {};
      });

    return result;
  }

  private getRolePriority(role: string): number {
    const index = this.applicationRoleOrder.indexOf(role);

    return index === -1 ? 0 : index;
  }


}
